import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { menuInfo } from "@shared/type/CommonType";
import { global } from "@shared/libs/Global";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { fnGetDataSet } from "@shared/api/restApi";
import SearchPanel from "@widgets/SearchPanel";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SnForm from "@widgets/SnForm";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAL_SAO_REP_002_TEX = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const formRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    freeform1: React.createRef(),
  });
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
    grid3: React.createRef<SnGridRef>(),
  });
  const fileHelperRef = useRef<any>(null);

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [formControls, setFormControls] = useState<any>({
    freeform1: [],
  });
  const [formDatas, setFormDatas] = useState<any>({
    freeform1: null,
  });
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
    grid3: [],
  });
  const [headerInfo, setHeaderInfo] = useState<any>({
    grid1: {},
    grid2: {},
    grid3: {},
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");

  useEffect(() => {
    // 품목팝업 품목유형 원단
    searchRef.current?.applyPopupConditions?.({
      cd_item1: { tp_item: "FAB" },
      cd_item2: { tp_item: "YRN" },
    });
  }, []);

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData({});
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setFormControls({
          freeform1: rtn.form.freeform1,
        });
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
          grid3: rtn.grid.grid3,
        });
        setHeaderInfo({
          grid1: rtn.header.grid1,
          grid2: rtn.header.grid2,
          grid3: rtn.header.grid3,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const onSearch = async () => {
    try {
      const params = {
        id: "sn_sal_sao_rep_002_tex_01",
        params: searchRef.current.getFormdata(),
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      getGridCurrent("grid3").resetUpdate();

      const data: any = await fnGetDataSet(params);
      setFormDatas({
        freeform1: null,
      });
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
        grid3: data[2] || [],
      });
    } catch (error: any) {}
  };

  const onFocusedRowChanged = useCallback(
    (focusedRowData: any | null) => {
      setFormDatas({
        freeform1: focusedRowData,
      });
      const selectedRow = focusedRowData;
      if (selectedRow) {
        const no_so = selectedRow.no_so;
        setSelectedDocu(no_so);
      }
    },
    [formDatas]
  );

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {},
      },
      grid3: {
        columns: columns.grid3,
        dataSource: dataSources.grid3,
        events: {},
      },
    }),
    [columns, dataSources]
  );

  const forms = useMemo(
    () => ({
      freeform1: {
        controls: formControls.freeform1,
        data: formDatas.freeform1,
        gridref: "grid1",
        etcProps: {},
        events: {},
      },
    }),
    [formControls, formDatas]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="49%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single" }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"], gridRefs.current["grid3"]],
              columns: ["no_so", "no_style"],
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="5%">
          <SnForm
            ref={formRefs.current["freeform1"]}
            gridref={gridRefs.current["grid1"]}
            data={forms.freeform1.data}
            controls={forms.freeform1.controls}
            events={forms.freeform1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel3" orgSize="35%">
          <div className="flex w-full h-full gap-1">
            <SnResizeBar direction={"width"} id="panel4" orgSize="60%">
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "single" }}
                events={grids.grid2.events}
              />
            </SnResizeBar>
            <SnResizeBar direction={"width"} id="panel5" orgSize="39.5%">
              <SnGrid
                ref={gridRefs.current["grid3"]}
                columnConfig={grids.grid3.columns}
                rowData={grids.grid3.dataSource}
                etcProps={{ rowSelection: "single" }}
                events={grids.grid3.events}
              />
            </SnResizeBar>
          </div>
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
});

export default SN_SAL_SAO_REP_002_TEX;
